import { FormBuilder, FormGroup } from '@angular/forms';
import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';

/**
 * interfaces
 */
import {
    IUnitFormControls,
    ICreateUnitPayload,
    IUpdateUnitPayload,
} from '../../interfaces';

/**
 * models
 */
import { IUnitModel, IUnitChildModel, IUnitDetailsModel } from '../../models';

/**
 * enums
 */
import { IOperationModifyMode, OperationModifyMode } from '@core/enums/operation-mode';

/**
 * validators
 */
import { AppValidators } from '@forms/validators';

/**
 * Manages the reactive form state for the unit add/edit modal.
 * The record ID is NOT part of the form — it travels as a path parameter.
 */
@Injectable()
export class UnitFormService {

    // ─── Form ─────────────────────────────────────────────────────────────────

    /** The reactive form instance. */
    private _form!: FormGroup<IUnitFormControls>;

    /** Read-only access to the form group. */
    public get form(): FormGroup<IUnitFormControls> {
        return this._form;
    }

    // ─── Operation mode ───────────────────────────────────────────────────────

    private readonly _operationMode: WritableSignal<IOperationModifyMode | null> = signal(null);

    /** Read-only operation mode signal (Create | Update | null). */
    public readonly operationMode: Signal<IOperationModifyMode | null> = this._operationMode.asReadonly();

    /** True when the form is in create mode. */
    public readonly isCreationMode: Signal<boolean> = computed(() =>
        this.operationMode() === OperationModifyMode.Create
    );

    // ─── Loading ──────────────────────────────────────────────────────────────

    private readonly _isLoading: WritableSignal<boolean> = signal(false);

    /** True while an API call triggered by the form is in flight. */
    public readonly isLoading: Signal<boolean> = this._isLoading.asReadonly();

    // ─── Selected ID ──────────────────────────────────────────────────────────

    private readonly _selectedId: WritableSignal<string | null> = signal(null);

    /** ID of the record currently being edited; null in create mode. */
    public readonly selectedId: Signal<string | null> = this._selectedId.asReadonly();

    // ─── Related unit ─────────────────────────────────────────────────────────

    private readonly _relatedUnit: WritableSignal<IUnitModel | IUnitChildModel | null> = signal(null);

    /**
     * In create mode: the parent unit the new child will be added under.
     * In edit mode: the unit being edited (updated to {@link IUnitDetailsModel} once the API responds).
     */
    public readonly relatedUnit: Signal<IUnitModel | IUnitChildModel | null> = this._relatedUnit.asReadonly();

    /**
     * Target hierarchy level for the unit being added/edited.
     * - Create mode: `relatedUnit.level + 1` (or 1 when no parent).
     * - Edit mode: the existing unit's own level.
     */
    public readonly targetLevel: Signal<number> = computed(() => {
        const unit = this._relatedUnit();
        const isCreationMode = this.isCreationMode()
        if (!unit) return 1;
        return isCreationMode ? unit.level + 1 : unit.level;
    });

    // ─── Init ─────────────────────────────────────────────────────────────────

    /**
     * @param _fb - Angular form builder.
     */
    constructor(private readonly _fb: FormBuilder) {
        this._form = this._initForm();
    }

    // ─── Private ──────────────────────────────────────────────────────────────

    /**
     * Builds the typed reactive form group with validators and initial values.
     */
    private _initForm(): FormGroup<IUnitFormControls> {
        return this._fb.group<IUnitFormControls>({
            name:           this._fb.control<string | null>(null, [AppValidators.required, AppValidators.minLength(4), AppValidators.maxLength(64)]),
            description:    this._fb.control<string | null>(null, [AppValidators.required, AppValidators.minLength(4), AppValidators.maxLength(256)]),
            html_hex_color: this._fb.control<string | null>(null),
            type_id:        this._fb.control<string | null>(null, AppValidators.required),
            parent_id:      this._fb.control<string | null>(null),
            level:          this._fb.control<number | null>(null),
            icon_id:        this._fb.control<string | null>(null),
            latitude:       this._fb.control<string | null>(null),
            longitude:      this._fb.control<string | null>(null),
            x_coordinate:   this._fb.control<string | null>(null),
            y_coordinate:   this._fb.control<string | null>(null),
            active:         this._fb.control<boolean | null>(true),
        });
    }

    // ─── Public setters ───────────────────────────────────────────────────────

    /** Sets the operation mode (Create | Update). */
    public setOperationMode(mode: IOperationModifyMode): void {
        this._operationMode.set(mode);
    }

    /** Sets the loading flag. */
    public setLoading(value: boolean): void {
        this._isLoading.set(value);
    }

    /** Sets the ID of the record being edited. */
    public setSelectedId(id: string | null): void {
        this._selectedId.set(id);
    }

    /**
     * Sets the related unit context.
     *
     * - **Create mode**: pass the parent unit — patches `parent_id` and `level` on the form.
     * - **Edit mode**: pass the unit being edited — `level` and `parent_id` come from {@link patchValue}.
     *
     * @param unit - Parent model (create) or editing model (update), or null for root-level adds.
     */
    public setRelatedUnit(unit: IUnitModel | IUnitChildModel | null): void {
        this._relatedUnit.set(unit);
        if (this.isCreationMode()) {
            this._form.controls.parent_id.setValue(unit?.id ?? null);
            this._form.controls.level.setValue(unit ? unit.level + 1 : 1);
        }
    }

    /**
     * Patches the form with data from a unit details model (edit mode).
     * Reads raw DTO fields via the model's `unitDetailsDto` property.
     *
     * @param model - Full unit details model from GET units/{id}.
     */
    public patchValue(model: IUnitDetailsModel): void {
        const dto = model.dto;
        this._form.patchValue({
            name:           dto.name,
            description:    dto.description,
            html_hex_color: dto.html_hex_color,
            type_id:        dto.type.id,
            parent_id:      dto.parent_id,
            level:          dto.level,
            icon_id:        null,
            latitude:       dto.latitude,
            longitude:      dto.longitude,
            active:         dto.active,
        });
    }

    /** Resets the form to its initial empty state. */
    public resetForm(): void {
        this._form.reset({ active: true });
        this._relatedUnit.set(null);
    }

    /**
     * Builds the create payload from the current form raw value.
     * @returns {@link ICreateUnitPayload} ready to POST.
     */
    public buildCreatePayload(): ICreateUnitPayload {
        const v = this._form.getRawValue();
        return {
            name:           v.name!,
            description:    v.description ?? null,
            html_hex_color: v.html_hex_color ?? null,
            type_id:        v.type_id!,
            parent_id:      v.parent_id ?? null,
            level:          v.level!,
            icon_id:        v.icon_id ?? null,
            latitude:       v.latitude ?? 0,
            longitude:      v.longitude ?? 0,
            x_coordinate:   v.x_coordinate ?? 0,
            y_coordinate:   v.y_coordinate ?? 0,
        };
    }

    /**
     * Builds the update payload from the current form raw value.
     * @returns {@link IUpdateUnitPayload} ready to PUT.
     */
    public buildUpdatePayload(): IUpdateUnitPayload {
        return {
            ...this.buildCreatePayload(),
            active: this._form.getRawValue().active ?? true,
        };
    }

}
